import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  const isRTL = language === 'ar';

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, isRTL, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations
const translations = {
  en: {
    // Header
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.services': 'Services',
    'nav.products': 'Products',
    'nav.whyUs': 'Why Us',
    'nav.contact': 'Contact',
    'nav.getInTouch': 'Get In Touch',
    
    // Hero Section
    'hero.badge': 'Trusted Healthcare Partner',
    'hero.title1': 'AFAQ ALNABTH',
    'hero.title2': 'Medical Supplies',
    'hero.description': 'A leading establishment specializing in pharmaceutical warehousing, medical equipment trading, and comprehensive healthcare supply solutions.',
    'hero.highlight1': 'Pharmaceutical Warehousing',
    'hero.highlight2': 'Medical Equipment Supply',
    'hero.highlight3': 'Laboratory Solutions',
    'hero.cta1': 'Explore Our Services',
    'hero.cta2': 'Learn More',
    'hero.certified': 'Quality Certified',
    'hero.certifiedDesc': 'International Standards',
    'hero.experience': 'Years Experience',
    'hero.experienceDesc': 'In Healthcare Industry',
    
    // About Section
    'about.badge': 'About Us',
    'about.title1': 'Your Trusted Partner in',
    'about.title2': 'Healthcare Solutions',
    'about.intro': 'AFAQ ALNABTH MEDICAL SUPPLIES EST. is committed to delivering excellence in pharmaceutical and medical supply services.',
    'about.description': 'Established with a vision to become a leading force in the healthcare supply industry, we specialize in pharmaceutical warehousing, medical equipment trading, and comprehensive healthcare solutions. Our commitment to quality, compliance, and customer satisfaction sets us apart.',
    'about.mission': 'Our Mission',
    'about.missionDesc': 'To provide high-quality medical supplies and pharmaceutical products that meet the evolving needs of healthcare providers.',
    'about.vision': 'Our Vision',
    'about.visionDesc': 'To be the region\'s most trusted and reliable partner for medical and pharmaceutical supplies.',
    'about.values': 'Our Values',
    'about.valuesDesc': 'Integrity, Quality, Compliance, Customer Focus, and Continuous Improvement drive everything we do.',
    'about.stat1': 'Years Experience',
    'about.stat2': 'Healthcare Partners',
    'about.stat3': 'Products Available',
    'about.stat4': 'Customer Support',
    
    // Services Section
    'services.badge': 'Our Services',
    'services.title1': 'Comprehensive Healthcare',
    'services.title2': 'Solutions',
    'services.description': 'We offer a complete range of pharmaceutical and medical supply services to meet all your healthcare needs.',
    'services.learnMore': 'Learn More',
    'services.s1.title': 'Pharmaceutical Warehouse',
    'services.s1.desc': 'State-of-the-art storage facilities maintaining optimal conditions for pharmaceutical products with full regulatory compliance.',
    'services.s2.title': 'Pharmaceutical Chemicals Trade',
    'services.s2.desc': 'Reliable supply of high-quality pharmaceutical chemicals and raw materials for manufacturing and research purposes.',
    'services.s3.title': 'Medical & Pharmaceutical Preparations',
    'services.s3.desc': 'Comprehensive range of non-pharmaceutical medical and pharmaceutical preparations for healthcare providers.',
    'services.s4.title': 'Medical Equipment & Supplies',
    'services.s4.desc': 'Premium medical, therapeutic, and surgical equipment and supplies from trusted global manufacturers.',
    'services.s5.title': 'Paper Products Trade',
    'services.s5.desc': 'Quality medical-grade paper products including examination papers, sterilization wraps, and healthcare consumables.',
    'services.s6.title': 'Tools for People of Determination',
    'services.s6.desc': 'Specialized equipment, mobility aids, and assistive devices designed to enhance quality of life and independence.',
    'services.s7.title': 'Pharmacy Services',
    'services.s7.desc': 'Complete pharmacy operations support including dispensing solutions and pharmaceutical care services.',
    'services.s8.title': 'Laboratory Equipment & Supplies',
    'services.s8.desc': 'Advanced laboratory instruments, diagnostic equipment, and consumables for clinical and research laboratories.',
    
    // Products Section
    'products.badge': 'Our Products',
    'products.title': 'Medical Equipment & Supplies',
    'products.description': 'Explore our extensive range of medical and pharmaceutical products sourced from trusted manufacturers worldwide.',
    'products.viewProducts': 'View Details',
    'products.viewCatalog': 'View Full Catalog',
    'products.shareProduct': 'Share Product',
    'products.shareAll': 'Share All Products',
    'products.shareOptions': 'Share Options',
    'products.copyLink': 'Copy Link',
    'products.linkCopied': 'Product details copied! Ready to share.',
    'products.comingSoon': 'Product details coming soon',
    'products.placeholder.title': 'Product Name',
    'products.placeholder.desc': 'Product description will be added here. High-quality medical equipment meeting international standards.',
    'products.filterAll': 'All Products',
    'products.filterBeds': 'Beds',
    'products.filterChairs': 'Chairs',
    'products.filterEquipment': 'Equipment',
    'products.noProducts': 'No products found in this category',
    'products.cat1': 'Surgical Equipment',
    'products.cat2': 'Laboratory Supplies',
    'products.cat3': 'Pharmaceutical Products',
    'products.cat4': 'Mobility & Accessibility',
    'products.cat5': 'Diagnostic Equipment',
    'products.cat6': 'Medical Consumables',
    'products.items': 'Products',
    
    // Why Choose Us Section
    'whyUs.badge': 'Why Choose Us',
    'whyUs.title1': 'Excellence in Every',
    'whyUs.title2': 'Aspect',
    'whyUs.description': 'We are dedicated to providing exceptional service and high-quality products that healthcare providers can rely on.',
    'whyUs.quality': 'Quality Assured',
    'whyUs.qualityDesc': 'All products meet international quality standards and regulatory requirements.',
    'whyUs.team': 'Expert Team',
    'whyUs.teamDesc': 'Experienced professionals with deep knowledge in pharmaceutical and medical supplies.',
    'whyUs.supply': 'Reliable Supply Chain',
    'whyUs.supplyDesc': 'Efficient logistics ensuring timely delivery across the region.',
    'whyUs.compliance': 'Regulatory Compliance',
    'whyUs.complianceDesc': 'Full compliance with local and international healthcare regulations.',
    'whyUs.trusted': 'Trusted by Healthcare Providers',
    'whyUs.trustedDesc': 'Serving hospitals, clinics, and pharmacies',
    'whyUs.delivery': 'Fast & Reliable Delivery',
    'whyUs.deliveryDesc': 'On-time delivery across the region',
    'whyUs.guaranteed': 'Quality Guaranteed',
    'whyUs.guaranteedDesc': 'All products meet international standards',
    
    // Contact Section
    'contact.badge': 'Contact Us',
    'contact.title1': 'Get In Touch',
    'contact.title2': 'With Us',
    'contact.description': 'Have questions or need a quote? We\'re here to help. Reach out to us and our team will respond promptly.',
    'contact.location': 'Office Address',
    'contact.address.line1': 'Royal House Building, Office No. 15',
    'contact.address.line2': 'Floor M, Abu Hail St',
    'contact.address.line3': 'Deira, Dubai, UAE',
    'contact.phone': 'Phone Numbers',
    'contact.phone1': '+971 54 280 8859',
    'contact.phone2': '+971 50 295 1774',
    'contact.email': 'Email Address',
    'contact.hours': 'Working Hours',
    'contact.monFri': 'Monday – Friday',
    'contact.monFriTime': '9:00 AM – 6:00 PM',
    'contact.satSun': 'Saturday & Sunday',
    'contact.closed': 'Closed',
    'contact.findUs': 'Find Us',
    'contact.getDirections': 'Get Directions',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email Address',
    'contact.form.phone': 'Phone Number',
    'contact.form.company': 'Company/Organization',
    'contact.form.message': 'Message',
    'contact.form.namePlaceholder': 'Your name',
    'contact.form.emailPlaceholder': 'your@email.com',
    'contact.form.phonePlaceholder': '+971 XX XXX XXXX',
    'contact.form.companyPlaceholder': 'Your company name',
    'contact.form.messagePlaceholder': 'How can we help you? Tell us about your requirements...',
    'contact.form.submit': 'Send Message',
    'contact.form.sending': 'Sending...',
    'contact.form.success': 'Message sent successfully!',
    'contact.form.successDesc': 'We will get back to you within 24 hours.',
    
    // Footer
    'footer.description': 'Your trusted partner in pharmaceutical warehousing, medical equipment trading, and comprehensive healthcare supply solutions.',
    'footer.quickLinks': 'Quick Links',
    'footer.ourServices': 'Our Services',
    'footer.businessHours': 'Business Hours',
    'footer.urgent': 'Need urgent assistance?',
    'footer.support': '24/7 Support Available',
    'footer.rights': 'All rights reserved.',
    'footer.backToTop': 'Back to Top',
  },
  ar: {
    // Header
    'nav.home': 'الرئيسية',
    'nav.about': 'من نحن',
    'nav.services': 'خدماتنا',
    'nav.products': 'منتجاتنا',
    'nav.whyUs': 'لماذا نحن',
    'nav.contact': 'اتصل بنا',
    'nav.getInTouch': 'تواصل معنا',
    
    // Hero Section
    'hero.badge': 'شريكك الموثوق في الرعاية الصحية',
    'hero.title1': 'آفاق النبض',
    'hero.title2': 'للمستلزمات الطبية',
    'hero.description': 'مؤسسة رائدة متخصصة في تخزين الأدوية وتجارة المعدات الطبية وحلول الإمداد الصحي الشاملة.',
    'hero.highlight1': 'مستودعات الأدوية',
    'hero.highlight2': 'توريد المعدات الطبية',
    'hero.highlight3': 'حلول المختبرات',
    'hero.cta1': 'استكشف خدماتنا',
    'hero.cta2': 'اعرف المزيد',
    'hero.certified': 'جودة معتمدة',
    'hero.certifiedDesc': 'معايير دولية',
    'hero.experience': 'سنوات الخبرة',
    'hero.experienceDesc': 'في صناعة الرعاية الصحية',
    
    // About Section
    'about.badge': 'من نحن',
    'about.title1': 'شريكك الموثوق في',
    'about.title2': 'حلول الرعاية الصحية',
    'about.intro': 'مؤسسة آفاق النبض للمستلزمات الطبية ملتزمة بتقديم التميز في خدمات الإمداد الصيدلاني والطبي.',
    'about.description': 'تأسست برؤية لتصبح قوة رائدة في صناعة الإمداد الصحي، نتخصص في تخزين الأدوية وتجارة المعدات الطبية وحلول الرعاية الصحية الشاملة. التزامنا بالجودة والامتثال ورضا العملاء يميزنا عن غيرنا.',
    'about.mission': 'رسالتنا',
    'about.missionDesc': 'تقديم مستلزمات طبية ومنتجات صيدلانية عالية الجودة تلبي الاحتياجات المتطورة لمقدمي الرعاية الصحية.',
    'about.vision': 'رؤيتنا',
    'about.visionDesc': 'أن نكون الشريك الأكثر موثوقية في المنطقة للمستلزمات الطبية والصيدلانية.',
    'about.values': 'قيمنا',
    'about.valuesDesc': 'النزاهة والجودة والامتثال والتركيز على العملاء والتحسين المستمر تدفع كل ما نقوم به.',
    'about.stat1': 'سنوات الخبرة',
    'about.stat2': 'شركاء الرعاية الصحية',
    'about.stat3': 'المنتجات المتاحة',
    'about.stat4': 'دعم العملاء',
    
    // Services Section
    'services.badge': 'خدماتنا',
    'services.title1': 'حلول الرعاية الصحية',
    'services.title2': 'الشاملة',
    'services.description': 'نقدم مجموعة كاملة من خدمات الإمداد الصيدلاني والطبي لتلبية جميع احتياجاتك الصحية.',
    'services.learnMore': 'اعرف المزيد',
    'services.s1.title': 'مستودعات الأدوية',
    'services.s1.desc': 'مرافق تخزين حديثة تحافظ على الظروف المثلى للمنتجات الصيدلانية مع الامتثال التنظيمي الكامل.',
    'services.s2.title': 'تجارة المواد الكيميائية الصيدلانية',
    'services.s2.desc': 'توريد موثوق للمواد الكيميائية الصيدلانية عالية الجودة والمواد الخام للتصنيع والبحث.',
    'services.s3.title': 'المستحضرات الطبية والصيدلانية',
    'services.s3.desc': 'مجموعة شاملة من المستحضرات الطبية والصيدلانية غير الدوائية لمقدمي الرعاية الصحية.',
    'services.s4.title': 'المعدات والمستلزمات الطبية',
    'services.s4.desc': 'معدات طبية وعلاجية وجراحية ممتازة ومستلزمات من مصنعين عالميين موثوقين.',
    'services.s5.title': 'تجارة المنتجات الورقية',
    'services.s5.desc': 'منتجات ورقية طبية عالية الجودة تشمل أوراق الفحص ولفائف التعقيم والمستهلكات الصحية.',
    'services.s6.title': 'أدوات أصحاب الهمم',
    'services.s6.desc': 'معدات متخصصة ووسائل مساعدة على الحركة وأجهزة مساعدة مصممة لتحسين جودة الحياة والاستقلالية.',
    'services.s7.title': 'خدمات الصيدلية',
    'services.s7.desc': 'دعم شامل لعمليات الصيدلية بما في ذلك حلول الصرف وخدمات الرعاية الصيدلانية.',
    'services.s8.title': 'معدات ومستلزمات المختبرات',
    'services.s8.desc': 'أجهزة مختبرية متقدمة ومعدات تشخيصية ومستهلكات للمختبرات السريرية والبحثية.',
    
    // Products Section
    'products.badge': 'منتجاتنا',
    'products.title': 'المعدات والمستلزمات الطبية',
    'products.description': 'استكشف مجموعتنا الواسعة من المنتجات الطبية والصيدلانية من مصنعين موثوقين حول العالم.',
    'products.viewProducts': 'عرض التفاصيل',
    'products.viewCatalog': 'عرض الكتالوج الكامل',
    'products.shareProduct': 'مشاركة المنتج',
    'products.shareAll': 'مشاركة جميع المنتجات',
    'products.shareOptions': 'خيارات المشاركة',
    'products.copyLink': 'نسخ الرابط',
    'products.linkCopied': 'تم نسخ تفاصيل المنتج! جاهز للمشاركة.',
    'products.comingSoon': 'تفاصيل المنتج قريباً',
    'products.placeholder.title': 'اسم المنتج',
    'products.placeholder.desc': 'سيتم إضافة وصف المنتج هنا. معدات طبية عالية الجودة تلبي المعايير الدولية.',
    'products.filterAll': 'جميع المنتجات',
    'products.filterBeds': 'الأسرّة',
    'products.filterChairs': 'الكراسي',
    'products.filterEquipment': 'المعدات',
    'products.noProducts': 'لا توجد منتجات في هذه الفئة',
    'products.cat1': 'المعدات الجراحية',
    'products.cat2': 'مستلزمات المختبرات',
    'products.cat3': 'المنتجات الصيدلانية',
    'products.cat4': 'الحركة وإمكانية الوصول',
    'products.cat5': 'أجهزة التشخيص',
    'products.cat6': 'المستهلكات الطبية',
    'products.items': 'منتج',
    
    // Why Choose Us Section
    'whyUs.badge': 'لماذا تختارنا',
    'whyUs.title1': 'التميز في كل',
    'whyUs.title2': 'جانب',
    'whyUs.description': 'نحن ملتزمون بتقديم خدمة استثنائية ومنتجات عالية الجودة يمكن لمقدمي الرعاية الصحية الاعتماد عليها.',
    'whyUs.quality': 'جودة مضمونة',
    'whyUs.qualityDesc': 'جميع المنتجات تلبي معايير الجودة الدولية والمتطلبات التنظيمية.',
    'whyUs.team': 'فريق خبراء',
    'whyUs.teamDesc': 'محترفون ذوو خبرة عميقة في المستلزمات الصيدلانية والطبية.',
    'whyUs.supply': 'سلسلة توريد موثوقة',
    'whyUs.supplyDesc': 'لوجستيات فعالة تضمن التسليم في الوقت المحدد عبر المنطقة.',
    'whyUs.compliance': 'الامتثال التنظيمي',
    'whyUs.complianceDesc': 'امتثال كامل للوائح الرعاية الصحية المحلية والدولية.',
    'whyUs.trusted': 'موثوق من مقدمي الرعاية الصحية',
    'whyUs.trustedDesc': 'نخدم المستشفيات والعيادات والصيدليات',
    'whyUs.delivery': 'توصيل سريع وموثوق',
    'whyUs.deliveryDesc': 'توصيل في الوقت المحدد عبر المنطقة',
    'whyUs.guaranteed': 'جودة مضمونة',
    'whyUs.guaranteedDesc': 'جميع المنتجات تلبي المعايير الدولية',
    
    // Contact Section
    'contact.badge': 'اتصل بنا',
    'contact.title1': 'تواصل',
    'contact.title2': 'معنا',
    'contact.description': 'هل لديك أسئلة أو تحتاج عرض سعر؟ نحن هنا للمساعدة. تواصل معنا وسيرد فريقنا على الفور.',
    'contact.location': 'عنوان المكتب',
    'contact.address.line1': 'مبنى رويال هاوس، مكتب رقم 15',
    'contact.address.line2': 'الطابق M، شارع أبو هيل',
    'contact.address.line3': 'ديرة، دبي، الإمارات العربية المتحدة',
    'contact.phone': 'أرقام الهاتف',
    'contact.phone1': '+971 54 280 8859',
    'contact.phone2': '+971 50 295 1774',
    'contact.email': 'البريد الإلكتروني',
    'contact.hours': 'ساعات العمل',
    'contact.monFri': 'الإثنين – الجمعة',
    'contact.monFriTime': '9:00 صباحاً – 6:00 مساءً',
    'contact.satSun': 'السبت والأحد',
    'contact.closed': 'مغلق',
    'contact.findUs': 'موقعنا على الخريطة',
    'contact.getDirections': 'احصل على الاتجاهات',
    'contact.form.name': 'الاسم الكامل',
    'contact.form.email': 'البريد الإلكتروني',
    'contact.form.phone': 'رقم الهاتف',
    'contact.form.company': 'الشركة/المؤسسة',
    'contact.form.message': 'الرسالة',
    'contact.form.namePlaceholder': 'اسمك',
    'contact.form.emailPlaceholder': 'بريدك@الإلكتروني.com',
    'contact.form.phonePlaceholder': '+971 XX XXX XXXX',
    'contact.form.companyPlaceholder': 'اسم شركتك',
    'contact.form.messagePlaceholder': 'كيف يمكننا مساعدتك؟ أخبرنا عن متطلباتك...',
    'contact.form.submit': 'إرسال الرسالة',
    'contact.form.sending': 'جاري الإرسال...',
    'contact.form.success': 'تم إرسال الرسالة بنجاح!',
    'contact.form.successDesc': 'سنرد عليك خلال 24 ساعة.',
    
    // Footer
    'footer.description': 'شريكك الموثوق في تخزين الأدوية وتجارة المعدات الطبية وحلول الإمداد الصحي الشاملة.',
    'footer.quickLinks': 'روابط سريعة',
    'footer.ourServices': 'خدماتنا',
    'footer.businessHours': 'ساعات العمل',
    'footer.urgent': 'تحتاج مساعدة عاجلة؟',
    'footer.support': 'دعم متاح 24/7',
    'footer.rights': 'جميع الحقوق محفوظة.',
    'footer.backToTop': 'العودة للأعلى',
  },
};
